import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  Tooltip,
  Box
} from '@mui/material';
import HeaderHelpPage from '../../../components/HeaderHelpPage';
import FormSearch from '../../../components/FormSearch';
import { sendDataWithGet } from '../../../services/services';
import { showSuccessMsg, showErrorMsg } from '../../../utils/toast';
import moment from 'moment';
import { COLORS } from '../../../utils/colors';
import CircularLoader from '../../../components/circularLoader';
import Pagination from '../../../components/Pagination';

const currentYear = new Date().getFullYear();
const defaultValue = {
  year: currentYear,
};

const MonthlyReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(defaultValue);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50 });
  
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  
  const loadReports = (params = pagination, currentFilter = filter) => {
    setLoading(true);
    
    const yearString = currentFilter.year ? String(currentFilter.year).trim() : currentYear;
    
    const handleSuccess = (response) => {
      const allReports = [];
      
      if (response?.result) {
        response.result.forEach((file, index) => {
          const reportName = file.report_name || 'N/A';
          const reportPath = file.report_path || '';
          
          allReports.push({
            id: index + 1,
            file_name: reportName,
            upload_date_formatted: file.report_date ? moment(file.report_date).format('YYYY-MM') : moment().format('YYYY-MM'),
            report_path: reportPath,
          });
        });
      }
      
      setReports(allReports);
      setLoading(false);
      setPagination(params);
    };

    const handleError = () => {
      showErrorMsg('Failed to load monthly reports');
      setLoading(false);
      setReports([]);
    };

    const url = `/payout/report/monthly_reports/files_uploaded/?year=${yearString}`;
    
    sendDataWithGet(
      url,
      handleSuccess,
      () => {},
      () => {},
      handleError
    );
  };

  const onFilterSubmit = (fParams) => {
    const newParams = {
      ...fParams,
      year: fParams?.year != null ? String(fParams.year).trim() : "",
    };

    const newPagination = {
      ...pagination,
      page: 1,
    };

    setFilter(newParams);
    loadReports(newPagination, newParams);
  };

  useEffect(() => {
    onFilterSubmit(defaultValue);
  }, []);

  const handleChangePage = (event, newPage) => {
    setPagination({
      ...pagination,
      page: newPage + 1,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({
      ...pagination,
      page: 1,
      page_size: parseInt(event.target.value, 10),
    });
  };

  const handleFileNameClick = (report) => {
    setSelectedReport(report);
    setOpenDownloadDialog(true);
  };

  const handleDownloadConfirm = async () => {
    if (!selectedReport) return;
    
    try {
      await downloadFile(selectedReport);
    } catch (error) {
      showErrorMsg('Failed to download file');
    }
    
    setOpenDownloadDialog(false);
    setSelectedReport(null);
  };

  const handleDownloadCancel = () => {
    setOpenDownloadDialog(false);
    setSelectedReport(null);
  };

  const downloadFile = async (report) => {
    try {
      if (!report.file_name) {
        showErrorMsg('Report name is missing');
        return;
      }
      
      const reportPath = report.report_path;
      
      if (!reportPath) {
        showErrorMsg('Report path is missing');
        return;
      }
      
      const response = await api.post('/payout/report/download_file/', {
        report_path: reportPath,
        file_name: report.file_name,
        registered_country: 'SGP' // default backend fallback
      }, {
        responseType: 'blob'
      });

      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = report.file_name;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        showSuccessMsg(`Download completed: ${report.file_name}`);
      } else {
        showErrorMsg(`Download failed: ${response.status} ${response.statusText}`);
      }
      
    } catch (error) {
        showErrorMsg(`Download failed: ${error.response?.data?.detail || error.message}`);
      throw error;
    }
  };

  const schema = [
    {
      type: "year",
      label: "Year",
      param: "year",
    },
  ];

  const paginatedReports = reports.slice((pagination.page - 1) * pagination.page_size, (pagination.page - 1) * pagination.page_size + pagination.page_size);

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <HeaderHelpPage title="Monthly Reports" isShowLink={false} />
      
      <Box sx={{ mb: 2 }}>
        <FormSearch
          schema={schema}
          onSearch={onFilterSubmit}
          defaultValue={defaultValue}
        />
      </Box>

      {loading ? (
        <CircularLoader />
      ) : (
        <Card sx={{ borderRadius: 2, border: '1px solid #f0f0f0' }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      minWidth: 400,
                      width: '70%'
                    }}
                  >
                    File Name
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      minWidth: 150,
                      width: '30%'
                    }}
                  >
                    Report Date
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedReports.length === 0 ? (
                  <TableRow>
                    <TableCell align="center" sx={{ py: 3 }}>
                      <Typography
                        gutterBottom
                        align="center"
                        variant="subtitle1"
                      >
                        Not found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedReports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        <Tooltip title={report.file_name}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#1b6ac9',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              '&:hover': {
                                color: '#1976d2',
                                textDecoration: 'underline'
                              },
                              wordBreak: 'break-word',
                              lineHeight: 1.4
                            }}
                            onClick={() => handleFileNameClick(report)}
                          >
                            {report.file_name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {report.upload_date_formatted}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {reports.length > 0 && (
            <Pagination
              count={reports.length}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
      )}

      <Dialog
        open={openDownloadDialog}
        onClose={handleDownloadCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Download Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Do you want to download{' '}
            <Typography 
              component="span" 
              sx={{ 
                fontWeight: 600, 
                color: '#1b6ac9',
                wordBreak: 'break-word',
                whiteSpace: 'normal'
              }}
            >
              {selectedReport?.file_name}
            </Typography>
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleDownloadCancel} 
            variant="outlined"
            sx={{ mr: 1 }}
          >
            No
          </Button>
          <Button 
            onClick={handleDownloadConfirm} 
            variant="contained"
            sx={{ 
              backgroundColor: COLORS.OXYGEN7_PURPLE,
              '&:hover': {
                backgroundColor: 'rgba(91, 45, 130, 0.8)'
              }
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MonthlyReports;
